<template>
  <div
    v-if="currentWeek.length"
    class="h-full max-w-1600 mx-auto w-full relative"
    :class="[
      {'rounded-t-9 bg-canvas min-h-full' : isPaperShape},
      isDarkMode ? 'text-app-color': 'text-dark-600'
    ]"
  >
    <div class="mt-20 md:mt-0 px-20 md:pr-40 pb-200 mx-auto min-h-full flex-grow flex flex-col"
         :class="[
           isPoseidonTheme ? 'bg-notebooks-poseidon' : '',
           isPaperShape ? 'rounded-t-9 pt-40 md:pl-40 bg-canvas' : 'pt-31 md:pl-25 bg-book'
         ]"
    >
      <!-- header -->
      <div class="md:flex items-start justify-between mb-40 relative">
        <div>
          <div class="flex items-center justify-between">
            <h1 class="text-26 font-bold leading-none">Week Planner</h1>
            <div class="md:hidden flex items-center order-0 md:order-2">
              <Calendar class="mr-5" :current-date="currentDate" @dayclick="dayclick" />
              <ButtonModule
                prefix="icon-keyboard-arrow-left text-21"
                class="text-grey-300"
                :class="isDarkMode ? 'hover:text-white' : 'hover:text-dark-600'"

                @click="goToWeek(-1)"
              />
              <div class="rotateY-180 flex">
                <ButtonModule
                  prefix="icon-keyboard-arrow-left text-21"
                  class="text-grey-300"
                  :class="isDarkMode ? 'hover:text-white' : 'hover:text-dark-600'"
                  @click="goToWeek(1)"
                />
              </div>
            </div>
          </div>
          <div class="font-medium text-dark-600 mt-4">
            <p class="text-16">
              {{ currentMonthName }} {{ currentYear }}
              <span class="mx-5">•</span>
              Week {{ currentWeekNumber }}
            </p>
          </div>
        </div>
        <div class="flex items-center justify-between mt-15 md:mt-0 self-start order-0 md:order-2">
          <FilterModal :tags="filteredTags" @toggleTag="filterByTag" />

          <ButtonModule
            v-if="!isCurrentDate"
            text="Back to today"
            class="text-link-color text-14 md:mx-10 px-1 font-medium border-b-3 border-link-color opacity-80 hover:opacity-100 anim-opacity"
            @click="goToWeek"
          />

          <div class="hidden md:flex items-center">
            <Calendar class="ml-15 md:ml-0 mr-10" :current-date="currentDate" @dayclick="dayclick" />
            <ButtonModule
              prefix="icon-keyboard-arrow-left text-21 p-3"
              class="text-grey-300 hover:bg-menu-hover rounded-full"
              :class="isDarkMode ? 'hover:text-white' : 'hover:text-dark-600'"

              @click="goToWeek(-1)"
            />
            <div class="rotateY-180 flex">
              <ButtonModule
                prefix="icon-keyboard-arrow-left text-21 p-3"
                class="text-grey-300 hover:bg-menu-hover rounded-full"
                :class="isDarkMode ? 'hover:text-white' : 'hover:text-dark-600'"
                @click="goToWeek(1)"
              />
            </div>
          </div>
          <BooksSelect
            v-if="selectedBooks[0]"
            :selected-books="selectedBooks"
            class="md:ml-10 border-2 rounded-full w-165"
            :class="isDarkMode ? 'border-hover' : 'border-dark-600 hover:bg-grey-150'"
            :default-book-id="parseInt($route.query.spaces)"
            :options="options"
            @onSelect="onSelect"
          />
        </div>
      </div>

      <!-- <div class="flex flex-wrap justify-between -mx-10 mt-40"> -->
      <div class="container">
        <Day
          v-for="(item, index) of currentWeek"
          :key="item"
          :item="item"
          :day-index="index"
          :selected-books="selectedBooks"
          :last-week-date="currentWeek[6].startDate"
          :first-week-date="currentWeek[0].startDate"
          :class="`day${index}`"
          class="md:min-h-200"
        />
        <!-- upcoming -->
        <div class="upcomings">
          <Upcomings :start-date="currentWeek[6].startDate" @getWeek="getWeek" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Day from '@/components/week-planner/Day'
import Upcomings from '@/components/week-planner/Upcomings'
import FilterModal from '@/components/task-board/FilterModal'
import Calendar from '@/components/week-planner/Calendar'
import BooksSelect from '@/components/task-board/BooksSelect'

import { defineComponent, computed, ref, provide, onMounted, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default defineComponent({
  components: {
    ButtonModule,
    Day,
    Upcomings,
    FilterModal,
    Calendar,
    BooksSelect
  },

  setup () {
    const { state, getters, dispatch, commit } = useStore()
    const router = useRouter()
    const route = useRoute()

    const settings = computed(() => state.settings.settings)
    const showSidebar = computed(() => state.settings.showSidebar)
    const isPoseidonTheme = computed(() => state.settings.settings.theme === 'Poseidon')
    const isFreePlan = computed(() => getters.isFreePlan)
    const isDarkMode = computed(() => getters.isDarkMode)
    const books = computed(() => state.book.books.filter(b => !b.isSection))
    const headquarters = computed(() => state.team.headquarters)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isPaperShape = computed(() => getters.isPaperShape)

    const filteredTags = computed(() => {
      const usedTags = {}

      const filteredEvents = state.planner.allEvents.filter(e => selectedBooks.value.map(b => b.id).includes(e.bookId))

      filteredEvents.forEach(e => {
        usedTags[e.bookId] = []
      })

      filteredEvents.forEach(e => {
        const ids = new Set(usedTags[e.bookId].map(tag => tag.id))

        usedTags[e.bookId] = [...usedTags[e.bookId], ...e.event.eventTags.filter(d => !ids.has(d.id))]
      })

      return usedTags
    })

    let tagIds = null
    const currentDate = ref(new Date())
    const currentWeek = ref([])
    const currentDay = ref(null)
    const currentYear = ref(null)
    const currentMonth = ref(null)
    const currentWeekNumber = ref(0)
    const currentMonthName = ref(null)

    const dateFormat = computed(() => state.settings.dateFormat)

    const isCurrentDate = computed(() => {
      const date = new Date()
      const weekNum = getWeekNumber(date)

      return currentWeekNumber.value === weekNum &&
        currentMonth.value === date.getMonth() &&
        +currentYear.value === date.getFullYear()
    })

    const selectedBooks = ref([])

    const options = computed(() => {
      return books.value.map(book => {
        return {
          id: book.id,
          title: book.title
        }
      })
    })

    watch(dateFormat, () => getWeek(currentDate.value))

    onMounted(() => {
      nextTick(() => {
        const allBooks = books.value
        const bIds = route.query.spaces.split(',').map(i => parseInt(i))

        if (isTeamAccount.value) allBooks.push(headquarters.value)

        selectedBooks.value = allBooks.filter(b => bIds.includes(b.id))

        dispatch('getEventTags', allBooks.map(b => b.id))
        dispatch('getAllEvents', allBooks.map(b => b.id))
        dispatch('getBook', route.params.id).then(res => {
          if (isTeamAccount.value && res.id && res.chat?.id) {
            dispatch('getMessages', {
              bookId: res.id,
              chatId: res.chat.id
            })
          }
        })
        getWeek(currentDate.value)
      })
      // TODO: move to event date
      if (isFreePlan.value) {
        router.push({ name: 'Books' })
      }
    })

    function getWeekNumber (date) {
      const newDate = new Date(date)

      var d = new Date(Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate()))
      var dayNum = d.getDay() || 7
      d.setDate(d.getDate() + 4 - dayNum)
      var yearStart = new Date(d.getFullYear(), 0, 1)
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
    }

    function getWeek (current) {
      const newDate = new Date(current)
      const week = []

      currentWeekNumber.value = getWeekNumber(current)

      currentDay.value = newDate.getDate()
      currentMonth.value = newDate.getMonth()

      // Starting Monday not Sunday
      if (newDate.getDay() === 0) { // if today is Sunday
        newDate.setDate((newDate.getDate() - newDate.getDay() - 6))
      } else {
        newDate.setDate((newDate.getDate() - newDate.getDay() + 1))
      }

      for (var i = 0; i < 7; i++) {
        const day = new Date(newDate).toLocaleString('en-US', { day: '2-digit' })
        const dayName = new Date(newDate).toLocaleString('en-US', { weekday: 'long' })
        const month = new Date(newDate).toLocaleString('en-US', { month: '2-digit' })
        const monthName = new Date(newDate).toLocaleString('en-US', { month: 'long' })
        const year = new Date(newDate).toLocaleString('en-US', { year: 'numeric' })

        let date = `${day}.${month}`

        switch (dateFormat.value?.name) {
          case 'DD.MM.YYYY':
            date = `${day}.${month}`
            break
          case 'DD-MM-YYYY':
            date = `${day}-${month}`
            break
          case 'MM/DD/YYYY':
          case 'YYYY/MM/DD':
            date = `${month}/${day}`
            break
          case 'MM.DD.YYYY':
            date = `${month}.${day}`
            break
          default:
            date = `${day}.${month}`
            break
        }

        week.push({
          date: date,
          startDate: `${year}-${month}-${day}`,
          day: day,
          dayName: dayName,
          month: month,
          monthName: monthName,
          year: year
        })

        newDate.setDate(newDate.getDate() + 1)
      }

      currentWeek.value = week
      currentMonthName.value = week[0].monthName
      currentYear.value = week[0].year

      commit('toggleBookLoading', true)

      dispatch('getEventsByDate', {
        startDate: week[0].startDate,
        finishDate: week[6].startDate,
        tagIds: tagIds,
        bookIds: selectedBooks.value.map(b => b.id)
      }).finally(_ => commit('toggleBookLoading', false))

      dispatch('getUpcomingEvents', {
        startDate: week[6].startDate,
        limit: 50,
        bookIds: selectedBooks.value.map(b => b.id)
      })

      const main = document.getElementById('main')
      if (main) main.scrollTop = 0
    }

    function goToWeek (value) {
      const week = 60 * 60 * 24 * 7 * 1000
      if (value > 0) {
        currentDate.value = new Date(currentDate.value.getTime() + week)
      } else if (value < 0) {
        currentDate.value = new Date(currentDate.value.getTime() - week)
      } else {
        currentDate.value = new Date()
      }

      getWeek(currentDate.value)
    }

    function dayclick (data) {
      getWeek(new Date(data.id))
    }

    function filterByTag (ids) {
      tagIds = ids
      dispatch('getEventsByDate', {
        startDate: currentWeek.value[0].startDate,
        finishDate: currentWeek.value[6].startDate,
        tagIds: ids,
        bookIds: selectedBooks.value.map(b => b.id)
      }).finally(_ => commit('toggleBookLoading', false))
    }

    provide('week', () => currentWeek.value)

    async function onSelect (value) {
      selectedBooks.value = value
      getWeek(currentDate.value)
      router.replace({ query: { spaces: selectedBooks.value.map(b => b.id).join(',') } })
    }

    return {
      // data
      options,
      currentWeek,
      currentYear,
      currentDate,
      selectedBooks,
      currentMonthName,
      currentWeekNumber,
      // computed
      filteredTags,
      settings,
      isDarkMode,
      showSidebar,
      isPaperShape,
      isCurrentDate,
      isPoseidonTheme,
      // functions
      getWeek,
      dayclick,
      onSelect,
      goToWeek,
      filterByTag
    }
  }
})
</script>

<style scoped>
.container {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
  "day0"
  "day1"
  "day2"
  "day3"
  "day4"
  "day5"
  "day6"
  "upcomings";
  gap: 68px 30px;
  grid-auto-flow: row;
}

.day0 { grid-area: day0; }
.day1 { grid-area: day1; }
.day2 { grid-area: day2; }
.day3 { grid-area: day3; }
.day4 { grid-area: day4; }
.day5 { grid-area: day5; }
.day6 { grid-area: day6; }
.upcomings { grid-area: upcomings; }

@media (min-width: 575px) {
  .container {
    grid-template-columns: 1fr 1fr;

    grid-template-areas:
      "day0 day1"
      "day2 day3"
      "day4 day5"
      "day6 ."
      "upcomings upcomings";
  }
}

@media (min-width: 900px) {
  .container {
    grid-template-columns: 1fr 1fr 1fr;

    grid-template-areas:
      "day0 day1 day2"
      "day3 day4 day5"
      "day6 . ."
      "upcomings upcomings upcomings";
  }
}

@media (min-width: 1150px) {
  .container {
    grid-template-columns: 1fr 1fr 1fr 1fr;

    grid-template-areas:
      "day0 day1 day2 day3"
      "day4 day5 day6 ."
      "upcomings upcomings upcomings upcomings";
  }
}

@media (min-width: 1401px) {
  .container {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-template-areas:
    "day0 day1 day2 day3 day4"
    "day5 day6 upcomings upcomings upcomings";
  }
}

</style>
